/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * ML-DSA-65 wrapper. Wraps `@noble/post-quantum/ml-dsa.js` to provide a
 * stable call shape, route keypair randomness through the F3 seam, and
 * validate input sizes at the wrapper boundary (#102) so misuse fails
 * with typed errors instead of an implementation-specific noble crash
 * deep in the call stack.
 *
 * ML-DSA-65 (NIST level 3) is the chosen signing algorithm for the
 * redesign — paired with ML-KEM-768 to keep the entire post-quantum
 * surface at level 3.
 */

import { ml_dsa65 } from '@noble/post-quantum/ml-dsa.js';
import type { Random } from '../random.js';

const KEYGEN_SEED_BYTES = 32;

/** Fixed byte lengths per FIPS 204 ML-DSA-65 parameter set. */
const ML_DSA_PRIVATE_KEY_BYTES = 4032;
const ML_DSA_PUBLIC_KEY_BYTES = 1952;
const ML_DSA_SIGNATURE_BYTES = 3309;

/** Thrown when an ML-DSA-65 private key of the wrong length is supplied to `sign`. */
export class InvalidMlDsaPrivateKeyError extends Error {
  constructor(actual: number) {
    super(
      `ML-DSA-65 private key must be ${ML_DSA_PRIVATE_KEY_BYTES} bytes, got ${actual}`
    );
    this.name = 'InvalidMlDsaPrivateKeyError';
  }
}

/** Thrown when an ML-DSA-65 public key of the wrong length is supplied to `verify`. */
export class InvalidMlDsaPublicKeyError extends Error {
  constructor(actual: number) {
    super(
      `ML-DSA-65 public key must be ${ML_DSA_PUBLIC_KEY_BYTES} bytes, got ${actual}`
    );
    this.name = 'InvalidMlDsaPublicKeyError';
  }
}

/** Thrown when an ML-DSA-65 signature of the wrong length is supplied to `verify`. */
export class InvalidMlDsaSignatureError extends Error {
  constructor(actual: number) {
    super(
      `ML-DSA-65 signature must be ${ML_DSA_SIGNATURE_BYTES} bytes, got ${actual}`
    );
    this.name = 'InvalidMlDsaSignatureError';
  }
}

const requirePrivateKey = (k: Uint8Array): void => {
  if (k.length !== ML_DSA_PRIVATE_KEY_BYTES) {
    throw new InvalidMlDsaPrivateKeyError(k.length);
  }
};

const requirePublicKey = (k: Uint8Array): void => {
  if (k.length !== ML_DSA_PUBLIC_KEY_BYTES) {
    throw new InvalidMlDsaPublicKeyError(k.length);
  }
};

const requireSignature = (s: Uint8Array): void => {
  if (s.length !== ML_DSA_SIGNATURE_BYTES) {
    throw new InvalidMlDsaSignatureError(s.length);
  }
};

export interface MlDsaKeyPair {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}

/**
 * Generate an ML-DSA-65 keypair.
 *
 * @param rng - randomness source (use `defaultRandom` in production)
 * @returns 1952-byte public key + 4032-byte private key
 */
export const keyGen = (rng: Random): MlDsaKeyPair => {
  const seed = rng.getRandomBytes(KEYGEN_SEED_BYTES);
  const { secretKey, publicKey } = ml_dsa65.keygen(seed);
  return { privateKey: secretKey, publicKey };
};

/**
 * Sign a message with the supplied ML-DSA-65 private key.
 *
 * @param privateKey - 4032-byte signing key
 * @param message - bytes to sign (no length limit; commonly a hash digest)
 * @returns ~3309-byte signature
 * @throws {InvalidMlDsaPrivateKeyError} if `privateKey.length !== 4032`
 */
export const sign = (
  privateKey: Uint8Array,
  message: Uint8Array
): Uint8Array => {
  requirePrivateKey(privateKey);
  return ml_dsa65.sign(message, privateKey);
};

/**
 * Verify an ML-DSA-65 signature.
 *
 * @param publicKey - 1952-byte verification key
 * @param message - bytes that were claimed to be signed
 * @param signature - 3309-byte signature from `sign`
 * @returns true iff the signature verifies under publicKey for message
 * @throws {InvalidMlDsaPublicKeyError} if `publicKey.length !== 1952`
 * @throws {InvalidMlDsaSignatureError} if `signature.length !== 3309`
 */
export const verify = (
  publicKey: Uint8Array,
  message: Uint8Array,
  signature: Uint8Array
): boolean => {
  requirePublicKey(publicKey);
  requireSignature(signature);
  return ml_dsa65.verify(signature, message, publicKey);
};
